import React, { useState, useMemo, useEffect } from 'react';
import { Calendar, BookMarked, Filter, SortAsc, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { publications, categoryConfig } from '../../data/publications';
import type { Publication } from '../../types/publications';

type SortOption = 'a-z' | 'z-a' | 'newest' | 'oldest';

const ITEMS_PER_PAGE = 12;

const PublicationSkeleton = () => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden animate-pulse">
    <div className="w-full h-48 bg-slate-200" />
    <div className="p-6">
      <div className="flex items-center space-x-4 mb-3">
        <div className="h-4 w-24 bg-slate-200 rounded" />
        <div className="h-4 w-32 bg-slate-200 rounded" />
      </div>
      <div className="h-6 bg-slate-200 rounded mb-3 w-3/4" />
      <div className="space-y-2">
        <div className="h-4 bg-slate-200 rounded w-full" />
        <div className="h-4 bg-slate-200 rounded w-5/6" />
      </div>
    </div>
  </div>
);

export default function PublicationsList() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortOption, setSortOption] = useState<SortOption>('a-z');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const filteredAndSortedPublications = useMemo(() => {
    let filtered = selectedCategory === 'all'
      ? publications
      : publications.filter(pub => pub.category === selectedCategory);

    const sorted = [...filtered].sort((a, b) => {
      switch (sortOption) {
        case 'a-z':
          return a.title.localeCompare(b.title);
        case 'z-a':
          return b.title.localeCompare(a.title);
        case 'newest':
          return b.date.localeCompare(a.date);
        case 'oldest':
          return a.date.localeCompare(b.date);
        default:
          return 0;
      }
    });

    return sorted;
  }, [selectedCategory, sortOption]);

  const totalPages = Math.ceil(filteredAndSortedPublications.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPublications = filteredAndSortedPublications.slice(startIndex, endIndex);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [selectedCategory, sortOption]);

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleSortChange = (sort: SortOption) => {
    setSortOption(sort);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <div className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Publications
          </h1>
          <p className="text-xl text-gray-300">
            Browse our comprehensive collection of research and publications
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <Filter className="inline h-4 w-4 mr-1" />
              Filter by Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="all">All Categories ({publications.length})</option>
              {Object.entries(categoryConfig).map(([slug, config]) => {
                const count = publications.filter(p => p.category === slug).length;
                return (
                  <option key={slug} value={slug}>
                    {config.title} ({count})
                  </option>
                );
              })}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <SortAsc className="inline h-4 w-4 mr-1" />
              Sort by
            </label>
            <select
              value={sortOption}
              onChange={(e) => handleSortChange(e.target.value as SortOption)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="a-z">Title: A to Z</option>
              <option value="z-a">Title: Z to A</option>
              <option value="newest">Date: Newest First</option>
              <option value="oldest">Date: Oldest First</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <p className="text-slate-600">
            Showing <span className="font-semibold">{startIndex + 1}</span> to{' '}
            <span className="font-semibold">{Math.min(endIndex, filteredAndSortedPublications.length)}</span> of{' '}
            <span className="font-semibold">{filteredAndSortedPublications.length}</span> publications
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
              <PublicationSkeleton key={index} />
            ))}
          </div>
        ) : currentPublications.length === 0 ? (
          <div className="text-center py-16">
            <BookMarked className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-xl text-slate-500">No publications found</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentPublications.map((pub) => (
                <Link
                  key={pub.id}
                  to={`/publications/${pub.id}`}
                  className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition border border-slate-200 overflow-hidden"
                >
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={pub.coverImage}
                      alt={pub.title}
                      className="w-full h-48 object-cover transform group-hover:scale-105 transition duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-4 text-sm text-slate-500 mb-3">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>{pub.date}</span>
                      </div>
                      <div className="flex items-center">
                        <BookMarked className="h-4 w-4 mr-1" />
                        <span className="capitalize">
                          {categoryConfig[pub.category as keyof typeof categoryConfig]?.title || pub.category}
                        </span>
                      </div>
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-emerald-600 transition line-clamp-2">
                      {pub.title}
                    </h2>
                    <p className="text-slate-600 text-sm line-clamp-3">
                      {pub.abstract}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>

                <div className="flex gap-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-4 py-2 rounded-lg transition ${
                            currentPage === page
                              ? 'bg-emerald-500 text-white'
                              : 'border border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="px-2 py-2">...</span>;
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}